import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, TrendingUp, PieChart, BarChart3, Target, Zap, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';

const AnalyticsFeature = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: TrendingUp,
      title: 'Real-Time Dashboards',
      description: 'Live visualization of Employee costs, productivity metrics, attendance patterns, and operational KPIs updated every second.'
    },
    {
      icon: PieChart,
      title: 'Employee Cost Analytics',
      description: 'Track actual vs. budgeted Employee costs by department, location, project, and time period with drill-down capabilities.'
    },
    {
      icon: BarChart3,
      title: 'Productivity Metrics',
      description: 'Measure employee performance, output per Employee hour, efficiency trends, and identify top performers vs. underperformers.'
    },
    {
      icon: Target,
      title: 'Forecasting Engine',
      description: 'AI-powered predictions for staffing needs, Employee costs, and scheduling requirements based on historical patterns.'
    },
    {
      icon: Zap,
      title: 'Custom Reports',
      description: 'Build unlimited custom reports with drag-and-drop interface. Schedule automated delivery to stakeholders via email.'
    },
    {
      icon: CheckCircle2,
      title: 'Compliance Tracking',
      description: 'Monitor Employee law compliance, overtime violations, break violations, and certification expirations automatically.'
    }
  ];

  const benefits = [
    'Reduce Employee costs by 15-25% with data-driven decisions',
    '100+ pre-built report templates ready to use',
    'Export to Excel, PDF, CSV for further analysis',
    'Real-time alerts for budget overruns and anomalies',
    'Historical trend analysis for year-over-year comparison',
    'Department and location performance benchmarking',
    'Employee productivity scorecards and rankings',
    'Automated compliance reporting for audits',
    'Integration with business intelligence tools',
    'Mobile access to key metrics on-the-go'
  ];

  const useCases = [
    {
      title: 'Multi-Location Management',
      description: 'Compare performance across all your locations side-by-side. Identify best practices from top-performing sites and replicate across your organization. Spot underperforming locations instantly.'
    },
    {
      title: 'Executive Decision Making',
      description: 'Give leadership the insights they need for strategic planning. Forecast Employee costs for new projects, analyze ROI of staffing changes, and make data-driven hiring decisions with confidence.'
    },
    {
      title: 'Department Optimization',
      description: 'Drill down into individual department performance. Identify overstaffed or understaffed teams, optimize shift coverage, and balance workloads across your organization efficiently.'
    },
    {
      title: 'Compliance & Audit',
      description: 'Maintain audit-ready records with comprehensive reporting. Track Employee law compliance across jurisdictions, monitor overtime patterns, and generate reports for regulatory requirements instantly.'
    }
  ];

  return (
    <>
      <LandingNav />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-purple-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Powerful Analytics & Insights
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 mb-8 max-w-4xl mx-auto">
              Turn workforce data into actionable insights. Make smarter decisions with real-time analytics that reveal exactly where to optimize Employee costs and productivity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/signup')}
                className="bg-white text-purple-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all flex items-center justify-center"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
              <button 
                onClick={() => navigate('/contact')}
                className="bg-purple-500/20 border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-purple-500/30 transition-all"
              >
                Schedule Demo
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Screenshot Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-200">
            <img 
              src="/Analyticspage.png" 
              alt="Powerful Analytics & Insights Dashboard"
              className="w-full h-auto rounded-xl"
            />
          </div>
          <div className="grid grid-cols-3 gap-6 mt-8">
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">25%</div>
              <div className="text-gray-600">Cost Reduction</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">100+</div>
              <div className="text-gray-600">Report Templates</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">Real-Time</div>
              <div className="text-gray-600">Data Updates</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            Comprehensive Analytics Features
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            Every metric, report, and insight you need to optimize your workforce and reduce costs.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 border border-purple-100 hover:shadow-xl transition-all"
                >
                  <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Transform Data Into Decisions
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Stop guessing and start knowing. Our analytics platform reveals exactly where you're overspending and how to fix it.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">How It Works</h3>
              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Data Collection</h4>
                    <p className="text-gray-600">System automatically aggregates time, attendance, and scheduling data from all sources.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">AI Analysis</h4>
                    <p className="text-gray-600">Machine learning identifies patterns, anomalies, and optimization opportunities.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Visualization</h4>
                    <p className="text-gray-600">Insights displayed in intuitive dashboards, charts, and reports anyone can understand.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Action & Optimization</h4>
                    <p className="text-gray-600">Implement recommendations and track ROI with before/after comparisons.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            Analytics for Every Business Need
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            From frontline managers to C-suite executives, everyone gets the insights they need.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-50 to-purple-50 rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition-all"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{useCase.title}</h3>
                <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Start Making Smarter Decisions Today
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Join thousands of businesses saving 15-25% on Employee costs with data-driven insights.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/signup')}
              className="bg-white text-purple-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all"
            >
              Start Free 14-Day Trial
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-all"
            >
              Schedule a Demo
            </button>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default AnalyticsFeature;
